import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { formatWeight } from "@heraion/shared";

import { ChartCappedNote } from "../charts/ChartCappedNote";
import { PanelTitleRow } from "./InfoPill";
import { NoDataCard, StaleHint } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { panelDays, panelRange } from "./progressWindow";
import { TierOneTrendRow } from "./TierOneTrendRow";
import { rankRepsAtLoad, type RepsAtLoadEntry } from "./tierOneMath";
import type { ProgressDeps } from "./types";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { useSetsInWindow } from "./useWindowSets";
import { usePersistedCopy } from "../usePersistedCopy";
import { weightUnitLabels } from "../weightUnitLabels";
import { dateFormatter } from "@heraion/shared";
import { useDateLocale } from "../primitives/DateFormatContext";

// The second strength axis (measurement-contract §8): "at the load you keep
// coming back to, are the reps climbing at equal-or-better effort?". Renders the
// shared TierOneTrendRow (same three §3 states as the e1RM panel), but reads RAW
// sets — the session-result cache keeps only each session's top set and throws
// away the repeated-load work this axis lives on — so it fetches its own window
// through the shared useWindowSets hook.
export function RepsAtLoadTrendsPanel(deps: ProgressDeps) {
  const { theme, units, apiFetch, diskCache } = deps;
  const { range } = deps;
  const windowDays = panelDays(range);
  const { t, i18n } = useTranslation();
  // The translated unit word for every weight below (CN-26 ondata 2).
  // ⛔ MEMOISED on `t`, not rebuilt per render: the object below feeds a
  // `useMemo` dependency array, and a fresh identity every render would turn
  // that memo into a no-op. `t` changes when the language does, which is
  // exactly when these labels must change.
  const unitLabels = useMemo(() => weightUnitLabels(t), [t]);
  // catalog (+ its error) resolves the live exercise name through the slug, the
  // foreign key — the set's stamped name is only a deleted-row fallback. The
  // sets themselves come from the raw-window hook, not this cache.
  const { catalog, error: catalogError } = useSessionResultsFromDeps(deps);
  const getName = useExerciseNameBySlug(catalog);
  // The fallback reads the clock, so it is memoised on `range`: a fresh range
  // per render would refetch forever.
  const win = useMemo(() => panelRange(range), [range]);
  const { sets, error: setsError, capped } = useSetsInWindow(apiFetch, win);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  const dateLocale = useDateLocale();
  const dateFmt = useMemo(
    () => dateFormatter(dateLocale, { day: "2-digit", month: "short" }),
    [dateLocale],
  );

  // #397: the ranking is kept on disk, not the sets: at most
  // REPS_AT_LOAD_MAX_ROWS rows, against thousands of sets.
  const fresh = useMemo(
    () => (sets ? rankRepsAtLoad(sets, win.toMs, panelDays(win)) : null),
    [sets, win],
  );
  const disk = useMemo(
    () => diskCache?.<RepsAtLoadEntry[]>(`progress/repsAtLoad/${panelDays(win)}d`),
    [diskCache, win],
  );
  const entries = usePersistedCopy(disk, fresh);
  const ranked = useMemo(() => {
    if (!entries || !catalog) return null;
    return entries.map((r) => ({
      ...r,
      name: getName(r.slug, r.snapshot),
      loadLabel: formatWeight(r.loadKg, units, i18n.language, unitLabels),
    }));
  }, [entries, catalog, getName, units, i18n.language, unitLabels]);

  const title = t("mobile.progress.repsAtLoad.title", {
    defaultValue: "Reps at load",
  });
  const help = t("mobile.progress.repsAtLoad.help", {
    days: windowDays,
    defaultValue:
      "For the exercises where you keep training the same load, this tracks the reps at that fixed weight over {{days}} days: the strength gain the e1RM chart can't see on high-rep and machine work. Bars = reps at the load, one per session (tap a bar for its date). Arrow = trend: ↗ climbing, → flat, ↘ falling. Only effort-tagged sets count, so more reps can't just mean an easier day.",
  });
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  const error = catalogError || setsError;
  if (ranked === null) return <NoDataCard theme={theme} titleKey="repsAtLoad" error={error || null} />;

  if (ranked.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={title} body={help} period={period} />
        {error ? <StaleHint theme={theme} /> : null}
        <Text style={styles.empty}>
          {t("mobile.progress.empty", {
            defaultValue: "No recent activity to compare",
          })}
        </Text>
        {capped ? <ChartCappedNote theme={theme} /> : null}
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={title}
        body={help}
        period={period}
      />
      {error ? <StaleHint theme={theme} /> : null}
      {ranked.map((r) => (
        <TierOneTrendRow
          key={r.slug}
          theme={theme}
          // No pattern grouping here — the value reads as set notation
          // ("18 × 55 kg"), so the load lives in the value, not a caption.
          name={r.name}
          insufficient={!r.measured}
          reg={r.reg}
          value={t("mobile.progress.repsAtLoad.repsValue", {
            reps: r.latestReps,
            load: r.loadLabel,
            defaultValue: "{{reps}} × {{load}}",
          })}
          notMeasurableText={t("mobile.progress.repsAtLoad.notMeasurable", {
            name: r.name,
            load: r.loadLabel,
            defaultValue:
              "Not measurable yet: your {{name}} sets at {{load}} aren't tagged with effort, so more reps might just mean an easier day. A set taken near failure, or an effort tag, would show your strength here.",
          })}
          fewDataText={t("mobile.progress.repsAtLoad.fewData", {
            days: windowDays,
            defaultValue: "Few data points in {{days}} d",
          })}
          trendLabels={{
            // The same three words the panel's own help legend teaches
            // for ↗ → ↘, so the arrow says out loud what the legend says
            // on screen.
            up: t("mobile.progress.repsAtLoad.a11yUp", { defaultValue: "climbing" }),
            flat: t("mobile.progress.repsAtLoad.a11yFlat", { defaultValue: "flat" }),
            down: t("mobile.progress.repsAtLoad.a11yDown", { defaultValue: "falling" }),
          }}
          chart={{
            key: r.slug,
            points: r.sorted.map((p) => ({
              x: p.atMs,
              y: p.reps,
              label: dateFmt.format(new Date(p.atMs)),
            })),
            yFormat: (n) => String(Math.round(n)),
            // The line above the bars says reps AND load, like the headline.
            readoutFormat: (n) =>
              t("mobile.progress.repsAtLoad.repsValue", {
                reps: Math.round(n),
                load: r.loadLabel,
                defaultValue: "{{reps}} × {{load}}",
              }),
            // Reps are a per-session COUNT, not a continuous estimate (PG-4).
            variant: "bars",
          }}
        />
      ))}
      {capped ? <ChartCappedNote theme={theme} /> : null}
    </View>
  );
}
